<template>
    <header v-if="largeHeader" id="header">
        <GuideTour />
        <div class="header-left-side">
            <div class="app-switcher-button" @click.stop="openAppSwitcher">
                <i class="material-icons">apps</i>
            </div>
            <div class="company-logo" @click="chatMainMenu('/')">
                <SupsisNewLogo />
            </div>
            <ChatStats class="supsis-chat-stats" v-if="!blockedConnection" />
        </div>
        <Search class="supsis-search-bar" />

        <HeaderRight @getSupportActivityInfo="isLiveSupportActive = $event" />

        <a-modal
            id="make-bug-report"
            :footer="null"
            :title="$t('message.sendErrorFeedBack')"
            :visible="errorModalVisible"
            @cancel="errorModalVisible = false"
            @ok="errorModalVisible = false"
        >
            <ErrorReport :file-url="errorFileURl" @changeVisibility="errorModalVisible = false" />
        </a-modal>

        <Modal2
            v-if="newVersionAvailable && !antModalExists()"
            :visible="newVersionAvailable"
            :closable="true"
            :maskClosable="true"
            okText="Yeni Sürümü Yükle"
            title="Yeni Sürüm Mevcut"
            @ok="reloadPage"
            @cancel="newVersionAvailable = false"
        >
            <div class="new-version-modal-content">
                <NewVersionUpdate class="new-version-icon" />
                <p>Yeni bir sürüm mevcut. Lütfen yeni sürüme geçmek için aşağıdaki butona tıklayın.</p>
                <p>Mevcut Sürüm: {{ versionInfo.versionStr }}</p>
            </div>
        </Modal2>
        <AppSwitcher v-if="appSwitcherVisible" @close="() => (appSwitcherVisible = false)" />
    </header>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faCheckCircle,
    faClock,
    faMinusCircle,
    faPowerOff,
    faSignOutAlt,
    faUserCog,
} from "@fortawesome/free-solid-svg-icons";
import ChatStats from "./ChatStats";
import Avatar from "vue-avatar";
import KeyboardShortcuts from "./KeyboardShortcuts";
import Version from "../components/svg/Version";
import Keyboard from "../components/svg/Keyboard";
import Online from "../components/svg/Online";
import Away from "../components/svg/Away";
import Busy from "../components/svg/Busy";
import Offline from "../components/svg/Offline";
import SocketConnect from "./svg/SocketConnect";
import { log2 } from "../utils/log2";
import CountryFlag from "vue-country-flag";
import Vue from "vue";
import { sendSocketMessage } from "../utils/socketStream";
import * as _ from "lodash";
import versionInfo from "../utils/version";
import { onNewVersionReceived } from "../utils/utils";
import NewVersionUpdate from "./svg/NewVersionUpdate.vue";
import ErrorReport from "./ErrorReport";
import HeaderRight from "./HeaderRight";
import CONFIG from "../config/config";
import Search from "./search/Search.vue";
import SupsisBadge from "./common/SupsisBadge.vue";
import TodayEvent from "../views/Pages/Calendar/Component/TodayEvent.vue";
import SupsisNewLogo from "./svg/SupsisNewLogo.vue";
import Modal2 from "./common/Modal2.vue";
import AppsIcon from "./svg/Apps.vue";
import AppSwitcher from "./AppSwitcher.vue";
import GuideTour from './common/GuideTour.vue'

library.add(faCheckCircle, faClock, faMinusCircle, faPowerOff, faUserCog, faSignOutAlt);
const log = log2("Header");
Vue.component("country-flag", CountryFlag);

export default {
    name: "Header",
    components: {
        SupsisNewLogo,
        TodayEvent,
        HeaderRight,
        ChatStats,
        Avatar,
        KeyboardShortcuts,
        Version,
        Keyboard,
        Online,
        Away,
        Busy,
        Offline,
        NewVersionUpdate,
        SocketConnect,
        ErrorReport,
        Search,
        SupsisBadge,
        Modal2,
        AppsIcon,
        AppSwitcher,
        GuideTour
    },
    data() {
        return {
            keyboardShortCutsModalVisible: false,
            zoomMaxRangeBound: 125,
            zoomMinRangeBound: 75,
            zoomValue: 100,
            modalVisible: false,
            versionInfo,
            newVersionAvailable: false,
            volume: -1,
            isOpenLang: false,
            customerDomainsFetched: false,
            cancels: [],
            newMessage: 0,
            errorModalVisible: false,
            errorFileURl: "",
            isLiveSupportActive: false,
            CONFIG,
            appSwitcherVisible: false,
            appSwitcherX: 0,
            appSwitcherY: 0,
        };
    },
    computed: {
        ...mapState({
            blockedConnection: (state) => state.app.blockedConnection,
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            menu: (state) => state.layout.menu,
            site: (state) => state.site.site,
            zoom: (state) => state.layout.zoom,
            settings: (state) => state.setting.settings,
            fakeMode: (state) => state.app.fakeMode,
            socketConnected: (state) => state.socketStatus.socket_connected,
            offlineMode: (state) => state.setting.offlineMode,
            debugMode: (state) => state.app.debugMode,
            socket: (state) => state.socketStatus.current_socket,
            visitorScreenUrl: (state) => {
                const isProduction = process.env.ENVIRONMENT === "production";
                const isStage = process.env.ENVIRONMENT === "stage";
                const siteAlias = (state.site.site.customerClientAddress || "").replace(".visitor.supsis.live", "");
                const protocol = isProduction || isStage ? "https" : "http";
                const hostname = isProduction
                    ? "demo-site.supsis.live?customer=" + siteAlias
                    : isStage
                    ? `demo-site.supsis.live?url=${protocol}://${
                          state.site.site.customerClientAddress || "betavisitor.supsis.live"
                      }/static/js/loader.js`
                    : "localsite.supsis.live?customer=local";
                return `${protocol}://${hostname}`;
            },
            customerDomains: (state) => state.visitor.customerDomains,
            popovers: (state) => state.app.headerPopoverStatus,
            privateChatIsOpen: (state) => state.app.privateChatIsOpen,
            unreadCount: (state) => state.app.unreadPrivateChatCount,
            largeHeader: (state) => state.app.largeHeader,
            allUnreadMailCounts: (state) => state.mail.allUnreadMailCounts,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        darkMode: {
            set(value) {
                this.updateDarkMode(value);
            },
            get() {
                return this.isDarkMode;
            },
        },
    },
    methods: {
        ...mapActions({
            setUserStatus: "auth/setStatus",
            changeZoom: "layout/changeZoom",
            refreshAuthUser: "auth/refreshAuthUser",
            getUsers: "user/getUsers",
            getUser: "user/getUser",
            updateUser: "user/updateUser",
            updateDarkMode: "user/updateDarkMode",
            togglePrivateChatVisibleState: "app/togglePrivateChatVisibleState",
            setChatFakeMode: "app/setChatFakeMode",
            getCustomerDomainsIfNeed: "visitor/getCustomerDomainsIfNeed",
            getAllUnreadMailCount: "mail/getAllUnreadMailCount",
            hideConversation: "chat/hideConversation",
        }),
        antModalExists() {
            return Array.from(document.querySelectorAll(".ant-modal-root")).filter((modal) => {
                return modal.style.display !== "none";
            }).length > 0;
        },
        chatMainMenu(path) {
            if (path) {
                this.$router.push(path);
            }
        },

        IsEmailEnabled() {
            return CONFIG.EMAIL_SERVICE_ENABLED;
        },
        toggleChat() {
            this.togglePrivateChatVisibleState();
        },
        previewApp() {
            const script = document.getElementById("previewScript");
            log("previewApp  delete script ", script);
            if (script) script.parentNode.removeChild(script);
            const lsOpenBtn = document.getElementById("ls-openButton");
            if (lsOpenBtn) lsOpenBtn.parentNode.removeChild(lsOpenBtn);
            const lsContainer = document.getElementById("ls-container");
            if (lsContainer) lsContainer.parentNode.removeChild(lsContainer);
            this.isLiveSupportActive = false;
        },

        integrationScript() {
            const isProduction = process.env.PRODUCTION === "true";
            const hostname = this.site.customerClientAddress;
            const protocol = isProduction ? "https" : "http";
            const path = isProduction ? "static/js/loader.js" : "loader.js";
            const commandHandlerScript =
                `<script>window.supsis=window.supsis||function(){(supsis.q=supsis.q||[]).push(arguments)};supsis.l=+new Date;` +
                ["<", "/", "script>"].join("").concat("\n");
            return (
                commandHandlerScript +
                `<script src=\"${protocol}://${hostname}/${path}\" type=\"text/javascript\" async defer>` +
                ["<", "/", "script>"].join("")
            );
        },
        integrationDemoScript() {
            return `<html>
<head>
    <meta name="viewport" content="initial-scale=1, maximum-scale=1, user-scalable=no">
    <title>Sitenizin Başlığı</title>
    ${this.integrationScript()}
</head>
<body>
 ......
</body>
</html>
`;
        },
        isSiteIntegrated() {
            return this.customerDomainsFetched && this.customerDomains.length;
        },
        reloadPage() {
            window.location.reload();
        },
        updateVolume: _.debounce(async function (volume, showNotification = true) {
            this.user.audioAlertVolume = volume / 100;
            await this.updateUser({ ...this.user });
            await this.refreshAuthUser();
            if (showNotification)
                this.$toasted.show(this.$t("message.userVolumeUpdated"), {
                    duration: 3000,
                    position: "bottom-center",
                });
        }, 1000),
        updateZoom: _.debounce(function (value) {
            this.changeZoom(value);
        }, 1000),
        disconnectSocket() {
            if (this.debugMode && this.socketConnected) {
                this.socket.disconnect();
            }
        },
        toggleKeyboardShotcutModal(visible) {
            log.debug("toggleKeyboardShotcutModal called", visible);
            this.keyboardShortCutsModalVisible = visible;
        },
        setStatus(status) {
            sendSocketMessage("user status-change", {
                userId: this.user._id,
                status: status,
                siteId: this.site._id,
            });

            this.setUserStatus(status);
        },
        openAppSwitcher(event) {
            this.appSwitcherVisible = !this.appSwitcherVisible;
            this.appSwitcherX = event.clientX;
            this.appSwitcherY = event.clientY;
        },
    },
    mounted() {
        log("mounted zoom", this.zoom);
        this.zoomValue = this.zoom;
        /* this.darkMode.isActive = this.isDarkMode*/
        document.body.style.zoom = this.zoom + "%";
        this.$forceUpdate();
        //Get new version information If ther is new version ask to reload with button
        onNewVersionReceived.then((x) => {
            this.newVersionAvailable = true;
        });
        this.getCustomerDomainsIfNeed().then((x) => {
            this.customerDomainsFetched = true;
        });
        if (Object.keys(this?.user || {}).length > 0) this.getAllUnreadMailCount(this.user?._id);
    },
    watch: {
        zoom(newValue) {
            log("zoom value changed", newValue);
            document.body.style.zoom = newValue + "%";
            this.$forceUpdate();
        },
        zoomValue(newValue, oldValue) {
            this.updateZoom(newValue);
        },
        volume(newValue, oldValue) {
            log.debug("volume change", { newValue, oldValue });
            if (newValue !== oldValue && oldValue !== -1) this.updateVolume(newValue, oldValue >= 0);
        },
        user(newUser) {
            log.debug("newUser", newUser);
            this.volume = newUser.audioAlertVolume ? newUser.audioAlertVolume * 100 : 0;
        },
        "popovers.isSettingPopoverOpen": function (newValue) {
            if (!newValue) {
                this.isOpenLang = false;
            }
        },
    },
};
</script>
<style lang="scss">
#header {
    .company-logo {
        transform: scale(1.1);
        transform-origin: center;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    @media (max-width: 965px) {
        .supsis-search-bar {
            display: none !important;
        }
    }
    @media (max-width: 665px) {
        .supsis-chat-stats {
            display: none !important;
        }
    }
    @media (max-width: 460px) {
        #header-right {
            #supsis-copilot,
            #popoverHelpMenu,
            #notifications {
                display: none !important;
            }
        }
    }
}
</style>
<style lang="scss" scoped>
.new-theme {
    .email-unread-count {
        margin-left: 22px;
        margin-top: -29px;
        color: white;
        font-weight: 600;
        z-index: 9;
        position: relative;
        margin-bottom: 13px;
        background-color: #ce0404;
        border-radius: 50%;
        width: 16px;
        height: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 8px;
    }

    .custom-button {
        width: 35px;
        min-width: 35px;
        height: 35px;
        background-color: #f3f6f9;
        border-radius: 8px;
        margin: 8px;
    }

    .custom-version-btn {
        height: 35px;
        background-color: #f3f6f9;
        border-radius: 8px;
        margin: 8px;
        text-transform: capitalize !important;
        display: flex;
        align-items: center;
        flex-direction: row;
        color: #448aff;
        border: none;

        svg {
            display: flex;
            align-items: center;
            fill: #448aff;
            margin-right: 4px;
        }
    }

    .avatarSection {
        display: flex;
        align-items: center;

        span {
            font-family: Poppins;
            font-size: 13px;
            color: #b5b5c3;

            strong {
                font-family: Poppins;
                font-size: 13px;
                color: #7e8299;
            }
        }

        .avatar {
            border-radius: 8px;
            width: 35px;
            height: 35px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: Poppins;
            font-size: 16px;

            &.online {
                color: #1bc5bd;
                background-color: #c9f7f5;
            }

            &.offline {
                color: #f64e60;
                background-color: #fdbec5;
            }

            &.away {
                color: #3699ff;
                background-color: #acd5ff;
            }

            &.busy {
                color: #ffa829;
                background-color: #ffe6c1;
            }
        }
    }
}

#email-icon-header {
    width: 35px;
    height: 35px;
    border-radius: 8px;
    margin: 8px;
    margin-right: 10px !important;
    margin: 0 !important;
    background: #f4f6f9;
    min-width: 35px !important;
}

.dark-mode {
    .custom-button {
        background-color: #343441;
    }
}
</style>
<style>
#close-live-support {
    cursor: pointer;
    animation-name: flash;
    animation-duration: 1.5s;
    z-index: 999999999999999;
    animation-iteration-count: infinite;
}

@keyframes flash {
    from {
        opacity: 0;
    }
    10%,
    90%,
    to {
        opacity: 0.2;
    }
    20%,
    80%,
    to {
        opacity: 0.4;
    }
    30%,
    70%,
    to {
        opacity: 0.6;
    }
    40%,
    60%,
    to {
        opacity: 0.8;
    }
    50%,
    to {
        opacity: 1;
    }
    0%,
    to {
        opacity: 0;
    }
}

.ant-popover-inner-content {
    box-shadow: 0 3px 6px #00000080;
}

.cursorPointer {
    cursor: pointer;
}

.cursorPointer:hover {
    font-weight: 600;
}

.popoverFont {
    font-family: Poppins;
    font-size: 14px;
    color: #181c32;
    font-weight: 300;
}

.new-version-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.new-version-icon {
    width: 100px;
    height: 100px;
    margin-bottom: 20px;
    color: #1890ff;
    fill: #1890ff;
}
</style>
